import { COUNTRIES_DATA } from '@fe/shared/Countries';
import { useRef, useState } from 'preact/hooks';

export interface T_NationalitySelectorProps {
  value?: string;
  onChange?: (isoCode: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  isInvalid?: boolean;
  language?: 'en' | 'ar';
}

const LABELS = {
  en: { placeholder: 'Select Nationality', search: 'Search...' },
  ar: { placeholder: 'اختر الجنسية', search: 'ابحث...' },
};

function getCountryName(isoCode: string, language: 'en' | 'ar'): string {
  try {
    const displayNames = new Intl.DisplayNames([language], { type: 'region' });
    return displayNames.of(isoCode) ?? isoCode;
  } catch {
    return isoCode;
  }
}

export default function NationalitySelector({
  value,
  onChange,
  placeholder,
  disabled,
  error,
  isInvalid,
  language = 'en',
}: T_NationalitySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const labels = LABELS[language];
  const isRtl = language === 'ar';

  const selectedCountry = value ? COUNTRIES_DATA.find((c) => c.isoCode === value) : undefined;
  const selectedName = selectedCountry
    ? getCountryName(selectedCountry.isoCode, language)
    : undefined;

  const filteredCountries = search
    ? COUNTRIES_DATA.filter((c) =>
        getCountryName(c.isoCode, language).toLowerCase().includes(search.toLowerCase()),
      )
    : COUNTRIES_DATA;

  const handleSelect = (isoCode: string) => {
    onChange?.(isoCode);
    setIsOpen(false);
    setSearch('');
  };

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => {
      if (!prev) setTimeout(() => inputRef.current?.focus(), 50);
      return !prev;
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <button
        type='button'
        disabled={disabled}
        onClick={handleToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          border: `2px solid ${isInvalid ? '#e53e3e' : '#e2e8f0'}`,
          borderRadius: '8px',
          background: 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '14px',
          textAlign: isRtl ? 'right' : 'left',
          direction: isRtl ? 'rtl' : 'ltr',
        }}
      >
        {selectedCountry ? (
          <>
            <img
              src={selectedCountry.flag}
              alt=''
              width={20}
              height={15}
              style={{ flexShrink: 0 }}
            />
            <span>{selectedName}</span>
          </>
        ) : (
          <span style={{ color: '#a0aec0' }}>{placeholder ?? labels.placeholder}</span>
        )}
      </button>

      {error && <p style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>{error}</p>}

      {isOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={handleClose} />
          <div
            dir={isRtl ? 'rtl' : 'ltr'}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 50,
              marginTop: '8px',
              width: '100%',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              background: 'white',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              height: '300px',
              padding: '8px 12px',
            }}
          >
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
              placeholder={labels.search}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                padding: '6px 10px',
                marginBottom: '8px',
                fontSize: '13px',
                outline: 'none',
                flexShrink: 0,
                background: 'white',
                direction: isRtl ? 'rtl' : 'ltr',
              }}
            />
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {filteredCountries.map((country) => (
                <button
                  key={country.isoCode}
                  type='button'
                  onClick={() => handleSelect(country.isoCode)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '6px 8px',
                    background: country.isoCode === value ? '#f7fafc' : 'white',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textAlign: isRtl ? 'right' : 'left',
                    borderRadius: '4px',
                  }}
                >
                  <img src={country.flag} alt='' width={20} height={15} style={{ flexShrink: 0 }} />
                  <span>{getCountryName(country.isoCode, language)}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
